import React, { useCallback, useRef } from "react";
import { Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, PageHeader } from "../../components/Common";
import PaginatedTable from "../../components/PaginatedTable";
import { useConfirm } from "../../hooks/useConfirm";
import { useMutations } from "../../hooks/useApiMutation";

const PackagesList = () => {
  const navigate = useNavigate();
  const { mutate } = useMutations();
  const { confirmAction } = useConfirm();
  const tableRef = useRef(null);

  const fetchData = useCallback(
    async (page, pageSize, search) => {
      let result = { data: [], total: 0 };

      await mutate("fetchPackages", {
        method: "get",
        endpoint: "/packages",
        params: { page, pageSize, search },
        onSuccess: (res) => {
          result = {
            data: res?.items || [],
            total: res?.totalCount || 0,
          };
        },
      });

      return result;
    },
    [mutate]
  );

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Tokens", dataIndex: "tokenCount", key: "tokenCount" },
    { title: "Period", dataIndex: "periodType", key: "periodType" },
    {
      title: "Base Price",
      dataIndex: "totalPayment",
      key: "totalPayment",
      render: (value, record) =>
        `${Number(value || 0).toFixed(2)} ${record.currencyCode || "EUR"}`,
    },
    {
      title: "Top-Up",
      key: "topup",
      render: (_, record) =>
        `${record.topUpTokenCount || 0} / ${Number(record.topUpPrice || 0).toFixed(2)} ${record.currencyCode || "EUR"}`,
    },
    {
      title: "Billing Multiplier",
      dataIndex: "billingMultiplier",
      key: "billingMultiplier",
    },
    {
      title: "Billing",
      key: "billingMode",
      render: (_, record) =>
        record.billingMode === "CostPlus"
          ? `Cost × ${Number(record.costMarkup || 0).toFixed(2)}`
          : "Tokens",
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      render: (value) => (value ? "Yes" : "No"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <>
          <Tooltip title="Edit">
            <Button
              variant="ghost"
              icon={<EditOutlined />}
              onClick={() =>
                navigate(`/packages/${record.packageId}/edit`, {
                  state: { record },
                })
              }
            />
          </Tooltip>
          <Tooltip title="Deactivate">
            <Button
              variant="ghost"
              danger
              icon={<DeleteOutlined />}
              onClick={() =>
                confirmAction({
                  key: "deletePackage",
                  method: "delete",
                  endpoint: `/packages/${record.packageId}`,
                  title: "Deactivate Package",
                  content: "Deactivate this package? Existing subscriptions keep their snapshotted terms.",
                  okText: "Deactivate",
                  okType: "danger",
                  onSuccess: () => tableRef.current?.reloadPage(),
                })
              }
            />
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Packages"
        subtitle="Token bundles and pricing plans offered to clients."
        extra={
          <Button
            variant="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/packages/new")}
          >
            Create Package
          </Button>
        }
      />
      <PaginatedTable
        ref={tableRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="packageId"
      />
    </div>
  );
};

export default PackagesList;
